import type { MetaDescriptor } from "react-router";
import { matchPath } from "react-router";
import {
  DEFAULT_SEO,
  toCanonical,
  type RobotsDirective,
} from "../components/SEO/applySEO";
import {
  LOCALIZED_SEO_PREFIX,
  PAGE_META_TAGS,
  type PageSEOConfig,
} from "../components/SEO/pageSEO";
import { Paths } from "../router/paths";
import { APP_LOCALES, splitLocalePath, withLocale } from "../i18n/localePath";
import {
  AUTHENTICATED,
  PAGE_SEO_KEY,
  PUBLIC_INDEXABLE,
  PUBLIC_NOINDEX,
  canonicalPathFor,
} from "../router/routeSeo";

type PathKey = keyof typeof Paths;
type Translate = (key: string) => string;

export interface SeoTags {
  title: string;
  description: string;
  keywords: string;
  image: string;
  type: string;
  canonical: string;
  robots: RobotsDirective;
}

/** Turn resolved SEO values into React Router meta descriptors. */
export const seoTagsToMeta = (tags: SeoTags): MetaDescriptor[] => [
  { title: tags.title },
  { name: "description", content: tags.description },
  { name: "keywords", content: tags.keywords },
  { name: "robots", content: tags.robots },
  { tagName: "link", rel: "canonical", href: tags.canonical },
  { property: "og:type", content: tags.type },
  { property: "og:url", content: tags.canonical },
  { property: "og:title", content: tags.title },
  { property: "og:description", content: tags.description },
  { property: "og:image", content: tags.image },
  { name: "twitter:url", content: tags.canonical },
  { name: "twitter:title", content: tags.title },
  { name: "twitter:description", content: tags.description },
  { name: "twitter:image", content: tags.image },
];

export const findRouteKey = (pathname: string): PathKey | undefined =>
  (Object.keys(Paths) as PathKey[]).find(
    (key) =>
      Paths[key] !== "*" &&
      matchPath({ path: Paths[key], end: true }, pathname) !== null,
  );

const includesKey = (list: readonly string[], key: PathKey) =>
  list.includes(key);

/**
 * Server-rendered <head> values for any URL, from the same tables the
 * client-side usePageSEO reads (routeSeo.ts, pageSEO.ts). Routes with their
 * own `meta` export (stock pages, articles) override this.
 */
export const resolveRouteSeo = (pathname: string, t: Translate): SeoTags => {
  const stripped = pathname.replace(/\/+$/, "") || "/";
  const bare = splitLocalePath(stripped).pathname.replace(/\/+$/, "") || "/";
  const key = findRouteKey(bare);
  const canonical = toCanonical(canonicalPathFor(stripped));
  const defaults: SeoTags = {
    title: DEFAULT_SEO.title,
    description: DEFAULT_SEO.description,
    keywords: DEFAULT_SEO.keywords,
    image: DEFAULT_SEO.image,
    type: DEFAULT_SEO.type,
    canonical,
    robots: "noindex, follow",
  };

  if (!key) return defaults;

  if (!includesKey(PUBLIC_INDEXABLE, key)) {
    // PUBLIC_NOINDEX and AUTHENTICATED pages are never indexed.
    const robots: RobotsDirective =
      includesKey(AUTHENTICATED, key) || includesKey(PUBLIC_NOINDEX, key)
        ? "noindex, follow"
        : defaults.robots;
    return { ...defaults, robots };
  }

  const seoKey = (PAGE_SEO_KEY as Partial<Record<PathKey, string>>)[key];
  const config: PageSEOConfig | undefined = seoKey
    ? PAGE_META_TAGS[seoKey]
    : undefined;
  if (!config) return { ...defaults, robots: "index, follow" };

  const prefix = seoKey ? LOCALIZED_SEO_PREFIX[seoKey] : undefined;
  // /ru and /uz pages: titles and descriptions from pages.seoMeta in that
  // language; English (and anything not translated yet) from PAGE_META_TAGS.
  const locale = splitLocalePath(stripped).locale;
  const translated = (field: "title" | "description") => {
    if (!seoKey || !locale || locale === "en") return undefined;
    const key = `pages.seoMeta.${seoKey}.${field}`;
    const value = t(key);
    return value && value !== key ? value : undefined;
  };
  return {
    title: prefix ? t(`${prefix}.title`) : (translated("title") ?? config.title),
    description: prefix
      ? t(`${prefix}.description`)
      : (translated("description") ?? config.description),
    keywords: prefix ? t(`${prefix}.keywords`) : config.keywords,
    image: config.ogImage || defaults.image,
    type: config.ogType ?? "website",
    canonical,
    robots: "index, follow",
  };
};

/** hreflang alternates for the three path locales, plus x-default on English. */
export const localeAlternateMeta = (pathname: string): MetaDescriptor[] => {
  const path = pathname.startsWith("http") ? new URL(pathname).pathname : pathname;
  const target = canonicalPathFor(splitLocalePath(path).pathname);
  return [
    ...APP_LOCALES.map((locale) => ({
      tagName: "link" as const,
      rel: "alternate",
      hrefLang: locale,
      href: toCanonical(withLocale(target, locale)),
    })),
    {
      tagName: "link" as const,
      rel: "alternate",
      hrefLang: "x-default",
      href: toCanonical(withLocale(target, "en")),
    },
  ];
};
